import type { WorkspaceWeight } from "@/components/courses/workspace/WorkspacePrimitives";
import { Tile } from "@/components/ui/tile";
import { cn } from "@/lib/utils";

export {
  DisclosureLine,
  LeadCard,
  QuietLine,
  SupportSection,
  WithdrawnNotice,
  WorkQueueRow,
} from "@/components/courses/workspace/WorkspacePrimitives";
export { WORKSPACE_WEIGHT_GRAMMAR } from "@/components/courses/workspace/workspace-weight-grammar";
export type { WorkspaceWeight } from "@/components/courses/workspace/WorkspacePrimitives";

type ElementProps<T extends keyof React.JSX.IntrinsicElements> = React.ComponentPropsWithoutRef<T>;

export function RegistrarSummaryStrip({ className, ...props }: ElementProps<"section">) {
  return (
    <section
      {...props}
      data-workspace-weight="support"
      className={cn(
        "grid min-w-0 gap-[var(--layout-bento-gap)] sm:grid-cols-2 xl:grid-cols-4",
        className,
      )}
    />
  );
}

export function RegistrarSummaryItem({
  label,
  value,
  detail,
  className,
}: {
  label: string;
  value: React.ReactNode;
  detail?: React.ReactNode;
  className?: string;
}) {
  return (
    <Tile as="div" className={cn("min-h-[5.5rem] justify-end gap-0.5", className)}>
      <span className="text-figure font-semibold tabular-nums text-foreground">{value}</span>
      <span className="min-w-0 break-words text-meta font-semibold text-foreground">{label}</span>
      {detail && <span className="break-words text-meta text-muted-foreground">{detail}</span>}
    </Tile>
  );
}

export function RegistrarRegisterLayout({ className, ...props }: ElementProps<"div">) {
  return (
    <div
      className={cn(
        "grid min-w-0 items-start gap-[var(--layout-bento-gap)] lg:grid-cols-[minmax(280px,344px)_minmax(0,1fr)]",
        className,
      )}
      {...props}
    />
  );
}

type RegistrarPanelWeight = Extract<WorkspaceWeight, "lead" | "support">;

export function RegistrarPanel({
  weight = "support",
  className,
  ...props
}: ElementProps<"section"> & { weight?: RegistrarPanelWeight }) {
  return (
    <Tile
      {...props}
      data-workspace-weight={weight}
      className={cn(
        "gap-0 overflow-hidden p-0",
        className,
      )}
    />
  );
}

export function RegistrarPanelHeader({ className, ...props }: ElementProps<"header">) {
  return <header className={cn("border-b border-rule py-4 ps-5 pe-4", className)} {...props} />;
}

export function RegistrarPanelBody({ className, ...props }: ElementProps<"div">) {
  return <div className={cn("min-w-0 py-4 ps-5 pe-4", className)} {...props} />;
}

export function RegistrarAuthorityNote({
  tone = "info",
  className,
  ...props
}: ElementProps<"aside"> & { tone?: "info" | "warning" | "neutral" }) {
  return (
    <aside
      {...props}
      data-workspace-weight={tone === "warning" ? undefined : "quiet"}
      className={cn(
        "text-ui",
        tone === "info" && "text-muted-foreground",
        tone === "warning" && "border-l-2 border-status-warning pl-4 text-foreground",
        tone === "neutral" && "text-muted-foreground",
        className,
      )}
    />
  );
}
